import React, { useState, useEffect } from 'react';

/**
 * SafetyConcernAlert component displays a notification when Pokket detects
 * potential mental health concerns in the conversation
 * 
 * @param {boolean} show - Whether to show the alert
 * @param {function} onDismiss - Function to call when dismissing the alert
 */
const SafetyConcernAlert = ({ show, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Handle visibility with animation
  useEffect(() => {
    if (show) {
      // Small delay to allow animation to work properly
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);
    }
  }, [show]);
  
  // Handle dismiss with animation
  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for fade-out animation to complete
    setTimeout(() => {
      if (onDismiss) onDismiss();
    }, 300);
  };
  
  if (!show) return null;
  
  return (
    <div 
      className={`fixed inset-x-0 top-16 mx-auto max-w-md p-4 bg-yellow-50 border border-yellow-200 rounded-md shadow-lg transition-all duration-300 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
      } z-50`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg 
            className="h-6 w-6 text-yellow-600" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm text-yellow-800 font-medium">
            Pokket has detected patterns that could indicate underlying mental health concerns. 
            While Pokket is trained on sport psychology principles, they are not a certified psychologist. 
            It's recommended you talk to a mental health professional to learn more.
          </p>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={handleDismiss}
              className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded-md text-sm font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyConcernAlert; 